import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ALPHA_URL, VERIFY_URL } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let pending = null;
const useExamples = (open) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return;
    pending = pending || fetch(`${API}/engine/examples`).then((r) => r.json()).catch(() => ({}));
    pending.then(setD);
  }, [open]);
  return d;
};

const pct = (x) => (x == null ? "—" : `${Number(x).toFixed(1)}%`);
const nice = (s) => (s || "").replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

const setupsEx = (d) => {
  const s = d?.setups;
  if (!s?.wr04) return null;
  return {
    rows: [["All-time A-grade setups", s.resolved?.toLocaleString()], ["Banked first profit (0.4R)", pct(s.wr04)], ["Ran to full 2R target", pct(s.wr2)], ...(s.day ? [[`Last full day (${s.day.day})`, `${s.day.n} setups · ${pct(s.day.wr04)} banked`]] : [])],
    link: [VERIFY_URL, "Audit every setup on Verify"],
  };
};
const regimeEx = (d) => {
  const r = d?.regime;
  if (!r) return null;
  return { rows: [["BTC regime right now", nice(r.label)], ["Model confidence", pct(r.conf * 100)], ["Stress risk next 15m", pct(r.p_stress_next * 100)], ["BTC at read", `$${Number(r.btc_close).toLocaleString()}`]], link: [ALPHA_URL + "/tq-regime", "Open TQ Regime"] };
};
const pulseEx = (d) => {
  const p = d?.pulse;
  if (!p) return null;
  return { rows: [["Top Pulse read now", p.coin], ["Pulse score", `${p.score}/100`], ["Phase", `${p.phase} · ${p.direction}`], ["Volume vs normal", `${Number(p.volume_ratio).toFixed(1)}×`]], link: [ALPHA_URL + "/screener", "Open TQ Pulse"] };
};
const researchEx = () => ({ preview: true, rows: [["Asset", "ETH"], ["Research score", "72/100 · worth watching"], ["Strongest factor", "Liquidity & depth (86)"], ["Weakest factor", "Holder concentration (41)"]], link: ["/research", "See the research lab"] });

export const EXPLAIN = {
  strat: { t: "Strategy engine", body: "Picture 10+ experienced traders, each watching for one specific pattern on every market, every 15 minutes. A setup only gets through when several filters agree and it clears the A-grade gate. Then it's published before anyone knows how it ends.", ex: setupsEx },
  models: { t: "Market models", body: "Before hunting for trades, the engine works out what kind of market this is: one of seven states, from a quiet grind to a stress event. Strategies that don't suit the current state are switched off, which avoids a lot of bad trades.", ex: regimeEx },
  research: { t: "Research models", body: "Scores breaking news by likely impact and grades assets on liquidity, supply unlocks, holder concentration and real activity, so you can tell if something deserves your capital or is just noise.", ex: researchEx },
  setups: { t: "Trade setups", body: "The output you act on: a buy or sell idea with entry, stop and target, plus the reasons behind it. Every one lands on Verify, so you can judge the engine by its record, not its marketing.", ex: setupsEx },
  pulse: { t: "Market Pulse", body: "A live shortlist of markets where something is building: unusual volume, shifting buy or sell pressure and tight price ranges. It shows where volatility is likely to arrive before the breakout.", ex: pulseEx },
  research_out: { t: "Research score", body: "One number and a short verdict that answer one question: is this asset worth it? It's built from the factors professionals check before committing capital.", ex: researchEx },
};

export const EngineExplainer = ({ id, onClose }) => {
  const open = Boolean(id);
  const d = useExamples(open);
  const e = id ? EXPLAIN[id] : null;
  const ex = e && (e.ex(d) || null);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="engine-explainer" className="max-w-lg rounded-none border border-white/[0.10] bg-[#0B0C0F] p-0 text-[#ECEBE4] shadow-[0_40px_120px_-30px_rgba(0,0,0,1)]">
        {e && (
          <div className="p-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#5A6170]">TQ engine · explained</p>
            <DialogTitle className="mt-3 font-display text-2xl font-semibold tracking-[-0.02em]">{e.t}</DialogTitle>
            <DialogDescription className="mt-3 text-[15px] leading-relaxed text-[#9DA3AE]">{e.body}</DialogDescription>
            <div className="mt-6 border border-white/[0.07]" data-testid="engine-explainer-example">
              <div className="flex items-center justify-between border-b border-white/[0.07] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em]">
                <span className="text-[#7D8390]">{ex?.preview ? "Example · full terminal preview" : "Real example · live data"}</span>
                {!ex?.preview && <span className="flex items-center gap-1.5 text-[#5A6170]"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#BACD1C]" />live</span>}
              </div>
              {ex ? ex.rows.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/[0.05] px-4 py-3 font-mono text-[12px] last:border-b-0">
                  <span className="text-[#7D8390]">{k}</span><span className="text-right tabular-nums text-[#ECEBE4]">{v}</span>
                </div>
              )) : <p className="px-4 py-5 font-mono text-[12px] text-[#5A6170]">{d ? "Live example unavailable right now." : "Loading live data…"}</p>}
            </div>
            {ex?.link && (
              <a href={ex.link[0]} data-testid="engine-explainer-link" className="group mt-6 inline-flex items-center gap-2 border-b border-white/30 pb-1 font-mono text-xs uppercase tracking-[0.16em] text-[#ECEBE4] hover:border-[#BACD1C] hover:text-[#BACD1C]">
                {ex.link[1]} <ArrowUpRight size={14} />
              </a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
